import './pdfjs-iterator-polyfill'
// ============================================================
// 静默已知的 3rd-party / 浏览器弃用警告(必须在所有 import 之前)
// ============================================================
// 1. Leaflet 1.9.4 内部 MouseEvent 处理会触发 Firefox/Chrome 自动弃用警告:
//    "MouseEvent.mozPressure 已弃用" / "MouseEvent.mozInputSource 已弃用"
//    Leaflet 1.9.4 已停止维护,这些属性仍能读但浏览器会刷屏。
//    升级到 1.9.x 新版或换 maplibre-gl 才能根治,先静默噪音。
// 2. Chrome DevTools "强制排版 / 无样式内容闪烁" 警告:任何代码在 stylesheet
//    应用前调用 getBoundingClientRect / offsetWidth 都会触发。
//    ProjectMap 的 useLayoutEffect 初始化时会读 layout,这是必要操作,无法避免;
//    只在 dev 工具面板里多一条警告,生产控制台无影响。
//    这里两种都静默,保留真正的业务错误。
if (typeof console !== 'undefined') {
  const _origWarn = console.warn.bind(console)
  const _origError = console.error.bind(console)
  const _isNoise = (firstArg) => {
    if (typeof firstArg !== 'string') return false
    return firstArg.includes('mozPressure')
      || firstArg.includes('mozInputSource')
      || firstArg.includes('强制排版')
      || firstArg.includes('无样式内容闪烁')
  }
  console.warn = (...args) => { if (_isNoise(args[0])) return; _origWarn(...args) }
  console.error = (...args) => { if (_isNoise(args[0])) return; _origError(...args) }
}

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import { cleanupCorruptedDrafts } from './utils/draftStorage'
import { ConfigProvider, App as AntApp, message } from 'antd'
import ErrorBoundary from './components/ErrorBoundary'
import { FriendlyErrorProvider } from './contexts/FriendlyErrorContext'
import zhCN from 'antd/locale/zh_CN'
import App from './App'
import './index.css'

// ============================================================
// Sentry 前端错误追踪(P0-3,2026-07-24)
// 配置:import.meta.env.VITE_SENTRY_DSN 有值就启用,没值完全 no-op
// 注册:https://sentry.io → 建 React 项目 → 复制 DSN
// Vite 把 VITE_SENTRY_DSN 注入到 bundle,prod build 时 tree-shaking 会去掉 no-op 分支
// 启用后自动捕获:
//   - 未捕获的 React 组件错误(ErrorBoundary 兜底)
//   - 未处理的 Promise rejection
//   - 网络请求 5xx 错误(配合 beforeSend 抽样)
// 关闭:把 VITE_SENTRY_DSN 留空,vite 会把整个 if 分支 dead-code elimination
// ============================================================
const _SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || ''
if (_SENTRY_DSN) {
  import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: _SENTRY_DSN,
      environment: import.meta.env.MODE,
      tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.05),
      // 不上报本地开发,避免开发异常堆满 Sentry quota
      beforeSend(event) {
        if (import.meta.env.DEV) return null
        return event
      },
    })
    // eslint-disable-next-line no-console
    console.info('[Sentry] 已启用 env=%s', import.meta.env.MODE)
  }).catch((e) => {
    // eslint-disable-next-line no-console
    console.warn('[Sentry] @sentry/react 加载失败,降级到 console.error:', e?.message)
  })
}

// R156-C-7: 之前 R155-9 只在 services/client.js 装 401 拦截器,但全仓 60+ 文件
// 直接 `import axios from 'axios'` 完全绕开 client.js(走 global axios 实例)。
// useAuth.jsx 已经装了一份全局 axios 401 拦截器(走 setOn401Handler 回调),
// 所有 import axios from 'axios' 都会触发。但 useAuth 的回调主要做 queryClient 清理,
// 没有跳登录页 — 这里在 entry 给全局 axios 加 401 → 跳登录的兜底,保证 32 个
// 裸用 axios 的页面在 cookie 过期时也能正确跳转。client.js 的拦截器变成
// 兼容性冗余(详见 R156-C-7)。
// 同时补上 X-Request-ID / X-Response-Time 写入 err(原 client.js 才有的能力),
// 让直接用 axios 的页面 query/mutation onError 也能拿到 rid/rt。
import axios from 'axios'

// R-fix-2026-08-23: access token 15min 过期,全局 axios 也接 refresh(原来只有 services/client.js 接了,
// 导致 81 个直接用 axios 的文件每 15 分钟被 401 踢回登录页)。并发去重。
let _refreshInflight = null
function _tryRefresh() {
  if (_refreshInflight) return _refreshInflight
  _refreshInflight = axios.post('/api/auth/refresh', null, {
    withCredentials: true,
    timeout: 10000,
  }).then(r => { _refreshInflight = null; return r }).catch(e => { _refreshInflight = null; throw e })
  return _refreshInflight
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    try {
      const rid = error?.response?.headers?.['x-request-id']
      const rt = error?.response?.headers?.['x-response-time']
      if (rid) error._rid = rid
      if (rt) error._rt = rt

      // R-fix-2026-08-21 P1 #4: 401 双重兜底
      // - 用 useAuth 的 _on401 回调(清 queryClient + user state),保证 redirect 前
      //   React Query 缓存已清,避免用户回退浏览器时看到 stale 权限(蓝/灰错位)
      // - 若 useAuth 还没挂载(_on401=null),fallback 直接 redirect
      // - 退出/登录过渡态(isLoggingOut/isLoggingIn)屏蔽 redirect,避免误跳
      const _redirect = () => {
        import('./hooks/useAuth').then(({ isLoggingOut, isLoggingIn, trigger401Handler }) => {
          if (isLoggingOut() || isLoggingIn()) return
          if (trigger401Handler) {
            trigger401Handler()
          } else {
            window.location.href = '/login?expired=1'
          }
        }).catch(() => {
          // dynamic import 失败兜底
          window.location.href = '/login?expired=1'
        })
      }

      if (error?.response?.status === 401) {
        const url = error?.config?.url || ''
        const isAuthEndpoint = url.includes('/api/auth/')
        const onLoginPage = window.location.pathname.startsWith('/login')
        // R-fix-2026-08-23: 先 refresh 换新 access_token 再重发原请求,refresh 失败才跳登录
        if (!isAuthEndpoint && !onLoginPage && !error.config?._retried) {
          try {
            await _tryRefresh()
            error.config._retried = true
            return axios(error.config)
          } catch (_) {
            _redirect()
          }
        } else if (!onLoginPage) {
          _redirect()
        }
      }
    } catch (_) { /* 拦截器里不抛 */ }
    return Promise.reject(error)
  }
)

// R8-A2-P0-5: 全局 query/mutation onError → console.error 留痕
// (生产可换 Sentry / 自建后端;短期 console + 后端 rid 关联)
// R16 R9 P0-4: 生产环境不再写 console。queryKey 常含项目/用户 ID,err.message
// 在 4xx 含 Pydantic 字段信息,第三方 analytics 脚本 / 浏览器扩展可读 console
// 钩子 → 泄露用户数据。开发期保留方便调试。
// R17 P0-F: 用 getErrorMessage 格式化 Pydantic 422 详情,而不是直接打 axios 裸 err.message
// ("Request failed with status code 422"),这样开发期控制台也能看到后端具体校验信息
import { getErrorMessage } from './utils'
import { STALE_TIME } from './constants/staleTime.js'
const _DEV = import.meta.env.DEV
// R-fix-2026-08-17 F-2 dedup: module-level Map 跟踪每个 project_id 最近 dispatch 时间
const _project404DispatchedAt = new Map()
const _onQueryError = (err, query) => {
  // R-fix-2026-08-17: project_id 404 fallback — 任意 /api/projects/{id} 返 404 时
  // 触发 window 事件,App.jsx 监听后 Navigate('/projects')。避免刷新时
  // react-query 缓存的 stale project_id 触发 100+ 个 404 噪声 + console 警告。
  // 触发条件:status 404 + URL 是 /api/projects/{数字}$ 模式(只针对项目详情,
  // 其他 404 仍走原逻辑)。
  if (err?.response?.status === 404) {
    // R-fix-2026-08-17: 扩展 stale project_id 触发源 — 之前正则只匹配 /api/projects/{id},
    // 但 100+ 个 404 实际来自 /api/schedule/{id}, /api/safety/...?project_id=N 等子资源
    const url = err?.config?.url || ''
    // 1) path 形式: /api/schedule/89, /api/design/drawings, /api/contracts/89/payments 等
    let m = url.match(/\/api\/(?:schedule|safety|quality|design|contract|contracts|meetings|procedures|points|kanban|workplan|cashflow|inspection|inspection-issues)\/(?:\d+|stats|config|quadrant|plans|data-audit|dependencies)\b(?:\/\d+)?(?:\?|$|#)/)
    // 2) project_id query param 形式: ?project_id=N 或 ?project_ids=...
    if (!m) {
      m = url.match(/[?&](?:project_id|project_ids)=(\d+)/)
    }
    // 3) path 形式: /api/projects/{id}/... (原逻辑)
    if (!m) {
      m = url.match(/\/api\/projects\/(\d+)/)
    }
    if (m) {
      const projectId = Number(m[1])
      // R-fix-2026-08-17 F-2 dedup: 1s 内同 project_id 只 dispatch 1 次
      // 之前 30+ 个端点同时 404 触发 30+ 个 window event + App.jsx 监听器跑 30+ 次 navigate
      const now = Date.now()
      const lastDispatched = _project404DispatchedAt.get(projectId) || 0
      if (now - lastDispatched < 1000) {
        return
      }
      _project404DispatchedAt.set(projectId, now)
      window.dispatchEvent(new CustomEvent('pmwork:project-404', {
        detail: { projectId, url }
      }))
      return
    }
  }
  // R-fix-2026-09-24: 401 + logout/login 过渡期 — 跳过 console.error 噪音
  // (这些 401 是预期的:cookie 已被清,请求在 in-flight 自然 401。client.js 已通过
  // isLoggingOut() 跳过 redirect,queryClient.cancelQueries() 已 cancel,但 axios XHR 已在网络层)
  // 401 + url 含 /api/auth/:任何用户会话里访问 auth 端点的 401 都是预期(未登录/会话过期)
  // 401 + 跨端点:可能是 logout/login 过渡期(request 已发出但 cookie 刚被清)—
  //   通过 window.__pmworkLoggingTransition(由 useAuth.jsx 设置/清除)判断
  const status = err?.response?.status
  if (status === 401) {
    const url = err?.config?.url || ''
    if (url.includes('/api/auth/')) return  // 所有 401 + /api/auth/ 都静默
    if (!_DEV && window.__pmworkLoggingTransition) return
    if (_DEV && window.__pmworkLoggingTransition) return
  }
  if (!_DEV) return
  // R128: 跳过 429 — 全局限流,各 mutation/query 已经在自己的 onError 里处理(打 console.warn 或不打扰用户)
  // R251-Audit R7: 但 dev 模式仍打 console.warn,让开发者能看到全局限流触发的请求,而不是默默丢
  if (err?.response?.status === 429) {
    console.warn('[query 429]', query.queryKey, getErrorMessage(err))
    return
  }
  // 取后端 X-Request-ID(axios 拦截器已经写到 err._rid),便于报修时告诉运维
  const rid = err?._rid || err?.response?.headers?.['x-request-id'] || '-'
  console.error('[query]', query.queryKey, 'rid=', rid, getErrorMessage(err))
}
const _onMutationError = (err, _vars, _ctx, mutation) => {
  if (!_DEV) return
  // R128: 同上,429 不再在全局 handler 重复打 console.error
  // R251-Audit R7: 但 dev 模式仍打 console.warn,便于调试限流场景
  if (err?.response?.status === 429) {
    console.warn('[mutation 429]', mutation.options?.mutationKey, getErrorMessage(err))
    return
  }
  const rid = err?._rid || err?.response?.headers?.['x-request-id'] || '-'
  console.error('[mutation]', mutation.options?.mutationKey, 'rid=', rid, getErrorMessage(err))
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: _onQueryError }),
  mutationCache: new MutationCache({ onError: _onMutationError }),
  defaultOptions: {
    queries: {
      // 修复:默认 retry 1 会把 401/403/404/422 等 4xx 也重发,浪费带宽 + 延迟错误反馈。
      // 只对 5xx 和网络错误重试,且最多 2 次
      retry: (failureCount, error) => {
        const status = error?.response?.status
        if (status && status >= 400 && status < 500) return false
        return failureCount < 2
      },
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME.DEFAULT
    }
  }
})

// R108 v5 测试期间临时挂载
if (typeof window !== 'undefined') {
  window.__QC__ = queryClient
}

// R145-Round8 F-09: 全局 unhandledrejection + window.error 兜底监听
// 之前只有 React Query onError,业务代码里 try/catch 漏掉或非 axios promise 抛错
// 会让用户看到空白页面(react ErrorBoundary 也未必兜住)。现在顶层 listener 弹
// message.warning + dev 期 console.error,生产不再写 console(避免 analytics 漏数据)
const _installGlobalErrorHandlers = () => {
  if (typeof window === 'undefined') return
  const _showToast = (txt) => {
    // message.success/warning 在 ConfigProvider 外也能用静态 API;但为了简洁只用静态 message
    try { message.warning(txt, 3) } catch { /* antd 未就绪时静默 */ }
  }
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason
    const status = reason?.response?.status
    // 4xx 由业务层处理;只对 5xx / 未知错误兜底
    if (status && status >= 400 && status < 500) return
    _showToast('请求异常,请稍后重试或刷新页面')
    if (_DEV) console.error('[unhandledrejection]', reason)
  })
  window.addEventListener('error', (e) => {
    // R-audit-2026-09-20-r3 P0-1:React.lazy() chunk 加载失败被 ErrorBoundary catch 不到
    // (Suspense 的 promise rejection 不冒泡到 React error boundary),在 window.error 兜底。
    // 用户看到「请刷新」+ reload 按钮,保留侧栏不被全白屏。
    const msg = String(e?.message || '')
    if (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('Loading CSS chunk')
    ) {
      _showToast('新版本发布,请刷新页面加载最新代码')
      // 立即 reload — chunk hash 已变,继续访问老 chunk 必坏
      setTimeout(() => window.location.reload(), 1500)
      return
    }
    if (_DEV) console.error('[window.error]', e?.message, e?.filename, e?.lineno)
  })
  // 网络断开/恢复友好提示:断网时业务请求会抛一堆超时错误,不如提前告知用户
  window.addEventListener('offline', () => {
    try { message.warning('网络已断开,请检查网络连接', 4) } catch { /* antd 未就绪时静默 */ }
  })
  window.addEventListener('online', () => {
    try { message.success('网络已恢复', 2) } catch { /* antd 未就绪时静默 */ }
  })
}

// 应用启动时清理损坏的 localStorage 草稿(旧版 dayjs 内部对象残留会导致 DatePicker 崩溃)
cleanupCorruptedDrafts()

// 全局 toast 时长:antd 默认 3s 偏短,审批/网络错误一闪而过
message.config({ duration: 4.5, maxCount: 3, top: 80 })

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        locale={zhCN}
        theme={{
          token: {
            borderRadiusSM: 2,
            borderRadius: 4,
            borderRadiusLG: 6,
            // R-audit-2026-08-30 R19 i18n-09: antd 默认 colorTextSecondary 是 #00000040(2.85:1)
            // 不满足 WCAG AA 4.5:1,提到 #595959(5.94:1)通过验证。覆盖全平台 259 处 #999 占位文字
            colorTextSecondary: '#595959',
            // R-2026-08-31 全面审计:品牌主色用 #2CA58D(原 antd 默认 #1677ff 蓝色与品牌绿冲突,
            // PillTabs 选中态 / card-green-border / KPI 角标都用品牌青)。antd 默认 primary 是 v5 蓝 #1677ff
            colorPrimary: '#2CA58D',
          },
        }}
        components={{
          Tooltip: {
            defaultProps: {
              mouseEnterDelay: 0.3,
            },
          },
          Popover: {
            defaultProps: {
              mouseEnterDelay: 0.3,
            },
          },
          Popconfirm: {
            defaultProps: {
              mouseEnterDelay: 0.3,
            },
          },
        }}
      >
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AntApp>
            {/* R-feat-2026-10-17 U4:5xx/网络错自动跳出友好重试 Modal,
                否则用户只能看到一闪而过的 message.error 不知下一步 */}
            <FriendlyErrorProvider>
              <ErrorBoundary>
                <App />
              </ErrorBoundary>
            </FriendlyErrorProvider>
          </AntApp>
        </BrowserRouter>
      </ConfigProvider>
    </QueryClientProvider>
  </React.StrictMode>
)

_installGlobalErrorHandlers()

// R-Phase2-A-1 2026-10-20:web-vitals 上报(原生 PerformanceObserver,无新依赖)
// 在 Sentry init 后调,web-vitals.js 内部会 import('@sentry/react') 接到 measurement
import('./utils/webVitals').then(({ installWebVitals }) => {
  installWebVitals()
}).catch(() => { /* 加载失败不阻断主流程 */ })
